import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { puedeUsarKIQ } from '../utils/permisosKiq';
import { useTheme } from '../context/ThemeContext';
import { HeaderProvider, useHeaderContext } from '../context/HeaderContext';
import ErrorBoundary from './ErrorBoundary';
import KIQButton from './design-system/KIQButton';
import KIQPanel from './kiq/KIQPanel';
import { brand, text, radius, typography, tint } from './design-system/tokens';
import {
  LayoutDashboard, Package, BookOpen, Tag,
  BarChart2, Users, LogOut, Receipt,
  GraduationCap, Settings, ScatterChart, Search, Sun, Moon,
  Menu, X, ShieldCheck, Target, Store, ChevronLeft, ChevronRight, Truck, Megaphone, FileText,
} from 'lucide-react';

// GastroKIQ Design System v1 — Fase 2 (App Shell). Reestiliza el shell
// (sidebar/header/nav/KIQ) con los tokens de Fase 1 — NUNCA cambia rutas,
// guards, roles ni permisos: cada condicional de visibilidad
// (`usuario?.rol === 'admin'`, `esSuperadmin`) es EXACTAMENTE la misma que
// ya existía antes de esta fase, solo reestilizada.
//
// Mismo umbral que ya usa Ventas.js para su propio layout interno (isMobile),
// para que ambos cambien de modo en el mismo ancho de pantalla. No se
// introdujo un breakpoint de "tablet" nuevo (sección 22 del pedido): la
// arquitectura existente ya es binaria mobile/desktop, y un modo tablet
// dedicado sería un proyecto de navegación aparte — tablets en portrait
// (~768-834px) ya caen en el drawer mobile, tablets en landscape (~1024px+)
// ya caen en el sidebar persistente de escritorio.
const MOBILE_BREAKPOINT = 900;

// Fase 3.4.16 — PHASE 3.4.15 (auditoría de arquitectura) concluyó que el
// sistema de 6 candidatos colisionables (Fases 3.4.10-3.4.14-R) resolvía el
// síntoma equivocado: ninguna cantidad de candidatos flotantes puede
// GARANTIZAR acceso persistente, porque el problema real vivía un nivel
// arriba — el <header> nunca estaba realmente fijo/sticky (ver la
// corrección de la cadena flex más abajo, "Fase 3.4.16 — shell scrolling"),
// así que cualquier fallback anclado ahí (el trigger de respaldo de
// 3.4.11-R) se volvía inalcanzable exactamente igual que el botón flotante.
// Con el <header> genuinamente fijo, un control DENTRO de él (ver
// KIQButtonHeader más abajo) es seguro por construcción — nunca puede
// solaparse con el contenido de la página, igual que ThemeToggle o el
// avatar, sus vecinos de siempre — y deja de hacer falta NINGÚN sistema de
// colisión en mobile. Todo useKiqSafePosition.js, sus 6 candidatos, sus
// observers (MutationObserver/ResizeObserver) y los atributos
// `data-kiq-protect` que alimentaban ese sistema se eliminan en esta fase
// (ver el reporte de Fase 3.4.16 para el detalle completo). El botón
// flotante de ESCRITORIO (bottom:24px/right:24px, sin cambios) nunca usó
// ese sistema — `activo` siempre fue `false` ahí — así que su preservación
// es automática, no requiere ningún camino especial.
const KIQ_SIZE_DESKTOP = 58;

const SIDEBAR_BG = 'var(--sidebar-bg)';
const SIDEBAR_BORDER = 'var(--sidebar-border)';
const TEXT = 'var(--text)';
const TEXT_DIM = 'var(--gray)';
const TEXT_FAINT = 'var(--gray-dim)';
// Shell (design_handoff_shell) — "Siempre oscuro, en ambos temas": el
// README lo pide explícitamente para el <aside> completo. Se implementa
// sobreescribiendo, SOLO dentro del sidebar, las custom properties CSS de
// las que dependen SIDEBAR_BG/SIDEBAR_BORDER/TEXT/TEXT_DIM/TEXT_FAINT/LINE2
// y el resto de estilos del sidebar (avatar, sectionLabel, .riq-nav-item:hover
// de public/index.html, etc.) — así ningún valor nuevo se inventa: son
// exactamente los mismos hex que YA usa el tema oscuro global en
// public/index.html, fijados aquí para que nunca cambien con el toggle de
// tema. `color` también se fija explícitamente: los nav items inactivos no
// traen `color` propio y heredan del `<aside>`, así que sin esto seguirían
// heredando el color del tema activo aunque el fondo ya fuera oscuro.
const SIDEBAR_DARK_VARS = {
  '--sidebar-bg': '#111319', '--sidebar-border': '#1E2430',
  '--text': '#F3F4F6', '--text-strong': '#F9FAFB',
  '--gray': '#9CA3AF', '--gray-dim': '#6B7280', '--gray-faint': '#4B5563',
  '--border': '#232B39', '--border-soft': '#1F2531',
  '--card-alt': '#14171F', '--card-hover': '#1B1F2A',
  '--chipbg': 'rgba(255,255,255,.05)', '--line2': 'rgba(255,255,255,.12)',
  color: '#F3F4F6',
};
// Identidad de marca vigente para acentos "clásicos" ya usados en el resto
// de la app (Dashboard, ProCard) — se conserva sin cambios (Fase 2 es SOLO
// shell, no un rebrand global de cada pantalla). El acento NUEVO de KIQ
// (brand.kiqBlue, de design-system/tokens.js) se reserva para el indicador
// de item activo y el propio KIQ — ver sección 9 del pedido.
const ACCENT = '#FF2D78';
// Fase 2.2 — el item activo del nav se pidió "menos agresivo visualmente,
// sigue siendo claramente visible" (Visual QA real). Se ajustó
// saturación/opacidad del mismo KIQ Blue, NO la arquitectura (sigue siendo
// tinte de fondo + barra izquierda de 3px, sección 5 del pedido): el fondo
// baja de 10% a 8% de opacidad y la barra pasa de sólida (100%) a 72% —
// sigue siendo un azul inequívoco y perfectamente visible, solo menos plano.
const ACTIVE_NAV_BG = tint(brand.kiqBlue, 0.08);
const ACTIVE_NAV_BORDER = tint(brand.kiqBlue, 0.72);
// Shell (design_handoff_shell) — mismos valores que --avatar-bg/-bd/-ink de
// public/index.html, ver comentario en UsuarioChipHeader de por qué viven
// aquí como constantes JS y no como var() inline.
const AVATAR_BG_DARK = 'rgba(255,255,255,.1)', AVATAR_BD_DARK = 'rgba(255,255,255,.16)', AVATAR_INK_DARK = '#F5F6FA';
const AVATAR_BG_LIGHT = 'rgba(20,22,58,.08)', AVATAR_BD_LIGHT = 'rgba(20,22,58,.18)', AVATAR_INK_LIGHT = '#14163A';
const LINE2 = 'var(--line2)';

// Shell (design_handoff_shell) — 3 items nuevos: KIQ (después de Panel,
// estilo especial vía `mark`/`sub`/`badge`), Proveedores (al final de
// OPERACIÓN) y Marketing (después de Objetivos). Ninguno cambia rutas/guards
// existentes — son entradas nuevas, no una reescritura de las que ya había.
const navGroups = [
  {
    label: 'OPERACIÓN',
    items: [
      { icono: LayoutDashboard, nombre: 'Panel', ruta: '/dashboard' },
      { nombre: 'KIQ', ruta: '/kiq', mark: true, sub: 'Recomendaciones' },
      { icono: Tag, nombre: 'Master Price', ruta: '/ingredientes', color: '#FF8FC4' },
      { icono: BookOpen, nombre: 'Recetario', ruta: '/recetas', color: '#7EA6FF' },
      { icono: ScatterChart, nombre: 'Ingeniería de Menú', ruta: '/menu-engineering', color: '#BBA4FF' },
      { icono: Package, nombre: 'Inventario', ruta: '/inventario', color: '#3DDC91' },
      // Fase 2B.2 — Financial Inbox: lectura de facturas/documentos (durables
      // y legado), sin ninguna acción de negocio todavía (eso llega en 2B.3).
      { icono: FileText, nombre: 'Facturas', ruta: '/facturas', color: '#4C86FF' },
      { icono: Receipt, nombre: 'Ventas y Caja', ruta: '/ventas', color: '#FFB65C' },
      { icono: Truck, nombre: 'Proveedores', ruta: '/proveedores', color: '#7EA6FF' },
    ],
  },
  {
    label: 'NEGOCIO',
    items: [
      { icono: BarChart2, nombre: 'Finanzas', ruta: '/finanzas', color: '#7EA6FF' },
      { icono: Target, nombre: 'Objetivos', ruta: '/objetivos', color: '#FF8FC4' },
      { icono: Megaphone, nombre: 'Marketing', ruta: '/marketing', color: '#FFB65C' },
      { icono: GraduationCap, nombre: 'Academia', ruta: '/academia', color: '#BBA4FF' },
      { icono: Users, nombre: 'Equipo', ruta: '/equipo', color: '#3DDC91' },
    ],
  },
];

const ROUTE_TITLES = {
  '/dashboard': ['Panel', 'Resumen de la operación'],
  '/kiq': ['KIQ', 'Recomendaciones'],
  '/ingredientes': ['Master Price', 'Ingredientes y proveedores'],
  '/recetas': ['Recetario', 'Costeo de recetas'],
  '/menu-engineering': ['Ingeniería de Menú', 'Rentabilidad por platillo'],
  '/inventario': ['Inventario', 'Existencias y conteos'],
  '/facturas': ['Facturas', 'Documentos y revisión de compras'],
  '/ventas': ['Ventas y Caja', ''],
  '/proveedores': ['Proveedores', 'Cumplimiento y costo por proveedor'],
  '/finanzas': ['Finanzas', 'P&L del restaurante'],
  '/objetivos': ['Objetivos', 'Metas vs. reales'],
  '/marketing': ['Marketing', 'Campañas y promociones'],
  '/academia': ['Academia', ''],
  '/academia/admin': ['Gestión de Academia', 'Administración global'],
  '/equipo': ['Equipo', 'Personal y horarios'],
  '/perfil': ['Configuración', ''],
  '/superadmin': ['Superadmin', 'Control privado del SaaS'],
  '/design-system': ['Design System', 'Referencia interna — no es una pantalla de producto'],
};

// Icono contextual del header por ruta — se reutilizan los MISMOS iconos que
// ya usa el sidebar (navGroups) para esa ruta, así nunca hay dos iconos
// distintos para la misma pantalla. `header?.icon` (usePageHeader) puede
// sobreescribirlo puntualmente; si una página no lo pasa, cae aquí sola —
// cero cambios necesarios en Dashboard/Ventas/Recetas/etc. para tener icono.
// KIQ no tiene `icono` (usa `KIQMark`, ver navItem más abajo) — el header
// simplemente no muestra caja de icono para esa ruta, nunca un icono falso.
const ROUTE_ICONS = Object.fromEntries(
  navGroups.flatMap(g => g.items.map(item => [item.ruta, item.icono]).filter(([, icono]) => icono))
);
ROUTE_ICONS['/academia/admin'] = Settings;
ROUTE_ICONS['/perfil'] = Settings;
ROUTE_ICONS['/superadmin'] = ShieldCheck;

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const esOscuro = theme === 'dark';
  return (
    <button onClick={toggleTheme} className="kiq-focusable" title={esOscuro ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'} style={{
      height: '32px', width: '32px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: radius.md, border: `1px solid ${LINE2}`, background: 'var(--card-alt)', color: TEXT_DIM, cursor: 'pointer',
    }}>
      {esOscuro ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
}

// Chip compacto de contexto de restaurante — SOLO texto, sin dropdown: no
// existe infraestructura real de selección/cambio de restaurante en la app
// hoy (un usuario pertenece a un único `restaurante`), así que un selector
// funcional sería fingir una función que no existe (sección 14 del pedido).
// Se oculta por completo si `restaurante` no llegó todavía (carga inicial)
// en vez de mostrar un nombre inventado.
function RestauranteChip({ restaurante }) {
  if (!restaurante?.nombre) return null;
  return (
    <div title={restaurante.nombre} style={{
      display: 'flex', alignItems: 'center', gap: '6px', height: '32px', padding: '0 10px',
      border: `1px solid ${LINE2}`, background: 'var(--card-alt)', borderRadius: radius.md,
      color: TEXT_DIM, fontSize: '12.5px', fontWeight: 600, maxWidth: '180px',
    }}>
      <Store size={13} style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{restaurante.nombre}</span>
    </div>
  );
}

// Presencia de usuario en el header — deliberadamente compacta (solo
// iniciales + nombre completo vía `title`), NO un segundo panel de usuario:
// el área detallada (nombre/rol/email/logout/plan) ya vive en el footer del
// sidebar y sigue siendo la fuente autoritativa — esto solo satisface el
// slot "User" del header conceptual (sección 6) sin duplicar esa tarjeta.
//
// Fase 2.2 — antes usaba brand.gradient completo (el mismo tratamiento que
// KIQ), lo que hacía competir visualmente "avatar de usuario" vs. "KIQ" en
// el mismo header (Visual QA real). Ahora es neutral y depende del tema
// (useTheme(), igual que ThemeToggle): claro = tinte navy suave + iniciales
// navy oscuro; oscuro = superficie ligeramente elevada + iniciales claras.
// El gradiente KIQ queda como propiedad visual exclusiva de KIQ.
function UsuarioChipHeader({ usuario }) {
  const { theme } = useTheme();
  if (!usuario) return null;
  const inicial = usuario.nombre?.charAt(0)?.toUpperCase() || '?';
  const esOscuro = theme === 'dark';
  // Valores literales — deliberadamente NO `var(--avatar-*)` inline: ver
  // comentario en public/index.html (Layout.test.js compara
  // elemento.style.background entre temas, y jsdom nunca resuelve una
  // var() a su valor real ahí). Mismos valores que --avatar-bg/-bd/-ink del
  // README (design_handoff_shell) por tema.
  const fondo = esOscuro ? AVATAR_BG_DARK : AVATAR_BG_LIGHT;
  const colorTexto = esOscuro ? AVATAR_INK_DARK : AVATAR_INK_LIGHT;
  const borde = esOscuro ? AVATAR_BD_DARK : AVATAR_BD_LIGHT;
  return (
    <div title={usuario.nombre || ''} aria-label={`Sesión de ${usuario.nombre || 'usuario'}`} style={{
      width: '32px', height: '32px', flexShrink: 0, borderRadius: radius.pill,
      background: fondo, color: colorTexto, border: `1px solid ${borde}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '12.5px', fontWeight: 800,
    }}>
      {inicial}
    </div>
  );
}

// Fase 3.4.19 (D1) — botón KIQ del header, extraído a su propio componente
// porque ahora se usa en un solo lugar dentro de la rama mobile de abajo
// (antes vivía inline en medio del grupo compartido mobile/desktop). Mismo
// JSX exacto que ya existía desde Fase 3.4.16 — cero cambio de
// comportamiento, solo de ubicación en el árbol.
function KiqHeaderButton({ kiqAbierto, onToggleKiq }) {
  return (
    <button
      type="button"
      onClick={onToggleKiq}
      className="kiq-focusable"
      aria-label="Abrir asistente KIQ"
      aria-expanded={kiqAbierto}
      title="KIQ"
      style={{
        height: '44px', width: '44px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: radius.md, border: `1px solid ${LINE2}`, background: 'var(--card-alt)', cursor: 'pointer',
      }}
    >
      <img src="/kiq-mark.png" alt="" style={{ width: '22px', height: '22px', borderRadius: '6px', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
    </button>
  );
}

function ShellHeader({ location, isMobile, onToggleSidebar, usuario, restaurante, kiqAbierto, onToggleKiq, kiqPermitido = true }) {
  const { header } = useHeaderContext();
  const [defaultTitle, defaultSub] = ROUTE_TITLES[location.pathname] || ['GastroKIQ', ''];
  const title = header?.title ?? defaultTitle;
  const subtitle = header?.subtitle ?? defaultSub;
  const HeaderIcon = header?.icon ?? ROUTE_ICONS[location.pathname];
  // Fase 3.4.19 (D1, auditoría de navegador real de Fase 3.4.18) —
  // PHASE 3.4.16 fijó el header al viewport correctamente, pero eso
  // convirtió su altura (antes algo que simplemente se scrolleaba fuera de
  // vista) en un costo PERMANENTE. A 320px, el header llegó a medir
  // 197-245px — hasta un 43% de un viewport de 568px de alto — porque el
  // grupo derecho (buscador genérico / `header.actions`, cada uno definido
  // por CADA página, nunca por este archivo) se repartía en flex-wrap
  // junto con KIQ/tema/avatar, generando un número de filas IMPREDECIBLE
  // según cuánto contenido traiga cada página (Ventas: 3 botones anchos +
  // subtítulo largo = hasta 4 filas; Recetas: 2 botones de acción +
  // subtítulo = 3 filas).
  //
  // Corrección — SOLO en mobile, el header pasa a una estructura de 2-3
  // filas ACOTADAS y predecibles (nunca flex-wrap abierto):
  //   Fila 1 (siempre, ~44px): hamburguesa + título (con ellipsis, nunca
  //     envuelve — el subtítulo ya no comparte fila con él) + KIQ + tema +
  //     avatar — los 4 controles son de tamaño FIJO, solo el título cede.
  //   Fila 2 (solo si hay subtítulo): el subtítulo solo, en su propia
  //     línea — se seguirá permitiendo que envuelva de forma natural si el
  //     texto es largo (Fase 3.4.6/A4: es información real — conteos,
  //     turno abierto — nunca se trunca), pero YA NO se mezcla con el
  //     ancho del título ni con los botones de acción, así que su propio
  //     wrap ya no dispara filas adicionales en cascada.
  //   Fila 3 (solo si la página NO optó por `header.actionsInPageOnMobile`
  //     y hay buscador genérico o `header.actions`): mismo contenido de
  //     siempre, sin cambios — las páginas que esta fase NO tocó
  //     (Ingredientes/Inventario/Ingeniería de Menú/Superadmin/Dashboard)
  //     siguen viendo exactamente el mismo contenido en el mismo lugar,
  //     cero regresión para ellas.
  //
  // `header.actionsInPageOnMobile` (nuevo, opcional, default false/
  // ausente = comportamiento IDÉNTICO al de antes de esta fase) — cuando
  // una página explícitam, deliberadamente, decide que SU PROPIO
  // `header.actions` es demasiado ancho para el header fijo en mobile Y
  // ya renderiza el mismo contenido dentro de su propio cuerpo scrolleable
  // (ver Recetas.js/Ventas.js, Fase 3.4.19), este header simplemente deja
  // de repetirlo aquí en mobile — nunca oculta un control sin una
  // alternativa alcanzable, solo evita la DUPLICACIÓN.
  const ocultarAccionesEnHeaderMobile = isMobile && !!header?.actionsInPageOnMobile;
  const hayAccionesGenericas = header?.search !== false || !!header?.actions;

  if (isMobile) {
    return (
      <header style={{
        flex: '0 0 auto', borderBottom: `1px solid ${SIDEBAR_BORDER}`,
        background: 'var(--header-bg)', backdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={onToggleSidebar}
            className="kiq-focusable"
            aria-label="Abrir menú de navegación"
            aria-expanded={false}
            aria-controls="riq-sidebar-nav"
            style={{
              height: '32px', width: '32px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: radius.md, border: `1px solid ${LINE2}`, background: 'var(--card-alt)', color: TEXT_DIM, cursor: 'pointer',
            }}
          >
            <Menu size={16} />
          </button>
          <h1 style={{
            margin: 0, flex: 1, minWidth: 0, fontSize: '16px', fontWeight: '800', letterSpacing: '-0.02em',
            color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{title}</h1>
          {kiqPermitido && <KiqHeaderButton kiqAbierto={kiqAbierto} onToggleKiq={onToggleKiq} />}
          <ThemeToggle />
          <UsuarioChipHeader usuario={usuario} />
        </div>
        {subtitle && (
          <span data-testid="kiq-header-subtitle" style={{ fontSize: '12px', color: TEXT_FAINT, whiteSpace: 'normal' }}>{subtitle}</span>
        )}
        {!ocultarAccionesEnHeaderMobile && hayAccionesGenericas && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', rowGap: '6px' }}>
            {header?.search !== false && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', height: '32px', padding: '0 10px', border: `1px solid ${LINE2}`, background: 'var(--card-alt)', borderRadius: radius.md, width: '100%' }}>
                <Search size={13} color={TEXT_FAINT} />
                <span style={{ fontSize: '12.5px', color: 'var(--gray-faint)' }}>{header?.searchPlaceholder || 'Buscar…'}</span>
              </div>
            )}
            {header?.actions}
          </div>
        )}
      </header>
    );
  }

  // Desktop — JSX SIN CAMBIOS respecto a antes de Fase 3.4.19 (ninguna
  // página de escritorio pierde ni reordena nada).
  return (
    <header style={{
      flex: '0 0 auto', minHeight: '58px', borderBottom: `1px solid ${SIDEBAR_BORDER}`,
      background: 'var(--header-bg)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', flexWrap: 'nowrap',
      gap: '16px', padding: '0 24px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        {HeaderIcon && (
          <span style={{ width: '26px', height: '26px', flexShrink: 0, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,45,120,.12)', color: ACCENT }}>
            <HeaderIcon size={14} />
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px', minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '800', letterSpacing: '-0.02em', color: TEXT, whiteSpace: 'nowrap' }}>{title}</h1>
          {subtitle && <span style={{ fontSize: '12px', color: TEXT_FAINT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</span>}
        </div>
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: '8px', gap: '8px' }}>
        {header?.search !== false && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', height: '32px', padding: '0 10px', border: `1px solid ${LINE2}`, background: 'var(--card-alt)', borderRadius: radius.md, width: '220px' }}>
            <Search size={13} color={TEXT_FAINT} />
            <span style={{ fontSize: '12.5px', color: 'var(--gray-faint)' }}>{header?.searchPlaceholder || 'Buscar…'}</span>
          </div>
        )}
        {header?.actions}
        <RestauranteChip restaurante={restaurante} />
        <ThemeToggle />
        <UsuarioChipHeader usuario={usuario} />
      </div>
    </header>
  );
}

export default function Layout({ children }) {
  const { usuario, restaurante, logout, esSuperadmin } = useAuth();
  // Beta — KIQ solo para admin/gerente (utils/permisosKiq.js, mismo criterio que el
  // backend): para cajero y cocinero no hay entrada en el menú, botón ni panel.
  const kiqPermitido = puedeUsarKIQ(usuario?.rol);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Fase 3 — KIQ Assistant Panel (sección 14 del pedido: Layout SOLO posee
  // la integración de abrir/cerrar; el panel en sí vive en components/kiq/,
  // no aquí). Reemplaza el placeholder no-op de Fase 2 (manejarClickKIQ).
  const [kiqAbierto, setKiqAbierto] = useState(false);
  // Fase 3.4.16 — `onToggleKiq` es la ÚNICA forma de abrir/cerrar KIQ,
  // compartida entre el botón del header (mobile, ShellHeader) y el botón
  // flotante (desktop, JSX al final de este componente) — un solo estado
  // (`kiqAbierto`), un solo <KIQPanel>, nunca una segunda instancia del
  // asistente. Reemplaza TODO el aparato de posicionamiento/colisión de
  // Fases 3.4.10-3.4.14-R (6 candidatos, useKiqSafePosition,
  // MutationObserver/ResizeObserver, traspaso de foco a la hamburguesa):
  // ya no hace falta nada de eso — ver el comentario junto a
  // KIQ_SIZE_DESKTOP arriba y el reporte de esta fase.
  const onToggleKiq = () => setKiqAbierto((abierto) => !abierto);
  // Shell (design_handoff_shell) — menú contraído (256px↔76px). Leído al
  // montar con try/catch (mismo patrón que ThemeContext.leerTemaInicial),
  // por defecto expandido. `contraido` (lo que de verdad controla el layout)
  // SIEMPRE es false en mobile, aunque la preferencia guardada sea true — el
  // drawer mobile siempre va expandido (el README lo pide explícitamente).
  const [colapsado, setColapsado] = useState(() => {
    try { return localStorage.getItem('riq_sidebar_colapsado') === '1'; } catch { return false; }
  });
  const contraido = colapsado && !isMobile;
  const alternarColapso = () => {
    setColapsado((prev) => {
      const siguiente = !prev;
      try { localStorage.setItem('riq_sidebar_colapsado', siguiente ? '1' : '0'); } catch {}
      return siguiente;
    });
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Si la pantalla deja de ser mobile (rotación, resize de ventana), el drawer
  // ya no aplica: nos aseguramos de que quede cerrado.
  useEffect(() => {
    if (!isMobile) setSidebarOpen(false);
  }, [isMobile]);

  // Cerrar el drawer de navegación Y el panel de KIQ automáticamente al
  // cambiar de ruta (Fase 3 — ninguno de los dos debe quedar abierto sobre
  // una página distinta a la que estaba cuando se abrió).
  useEffect(() => {
    setSidebarOpen(false);
    setKiqAbierto(false);
  }, [location.pathname]);

  // Bloquear el scroll del body mientras el drawer de navegación (mobile) O
  // el panel de KIQ (cualquier viewport) estén abiertos — UN SOLO efecto
  // dueño de document.body.style.overflow (Fase 3, sección 18: nunca dos
  // efectos independientes escribiendo la misma propiedad global, se pisan
  // entre sí). KIQPanel.js NO tiene su propio bloqueo de scroll.
  useEffect(() => {
    document.body.style.overflow = ((isMobile && sidebarOpen) || kiqAbierto) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, sidebarOpen, kiqAbierto]);

  // Cerrar con Escape.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sidebarOpen]);

  // Los ítems de navegación son <Link> reales (funcionan con teclado y click
  // medio/"abrir en pestaña nueva" de forma nativa); solo necesitamos cerrar
  // el drawer mobile al navegar.
  const cerrarDrawer = () => setSidebarOpen(false);

  return (
    <HeaderProvider>
      {/* Fase 3.4.16 — corrección de la arquitectura de scroll del shell
          (Fase 3.4.15, sección C: causa raíz confirmada). Antes:
          `minHeight:'100vh'` (sin tope) en este div + el <main> de abajo
          sin `minHeight:0` significaba que NADA en la cadena de ancestros
          tenía una altura realmente acotada — el <div style={{flex:1,
          overflow:'auto'}}> del contenido (ver más abajo) nunca llegaba a
          necesitar su propio scroll, porque todo el shell (header incluido,
          un simple hermano flex dentro de la MISMA columna) crecía sin
          límite y era el DOCUMENTO el que terminaba scrolleando — así el
          header (nunca sticky/fixed) se iba con él. `.kiq-app-shell` (ver
          <style> abajo) le pone a ESTE div una altura REALMENTE acotada al
          viewport (100dvh con fallback a 100vh — sección 3 del pedido:
          "Use 100dvh where appropriate, with a compatible fallback") +
          `overflow:'hidden'`, y el <main> de abajo gana `minHeight:0`
          (abajo) — con eso, el ÚNICO dueño de scroll pasa a ser,
          correctamente, el <div style={{flex:1, overflow:'auto'}}> del
          contenido: exactamente el diseño que esos estilos YA declaraban,
          solo que nunca se activaba. El <aside> (sidebar, `position:fixed`)
          y todo lo demás que ya es `position:fixed` (drawer overlay, botón
          flotante de escritorio, KIQPanel) es inmune a este cambio por
          construcción: `overflow:hidden` en un ancestro sin transform/
          filter/will-change NUNCA recorta a un descendiente
          `position:fixed` — sigue posicionándose contra el viewport real,
          nunca contra esta caja. */}
      <style>{`
        .kiq-app-shell { height: 100vh; overflow: hidden; }
        @supports (height: 100dvh) {
          .kiq-app-shell { height: 100dvh; }
        }
      `}</style>
      <div className="kiq-app-shell" style={{ display: 'flex', background: 'var(--bg)', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        {/* Overlay del drawer (solo mobile, solo si está abierto) */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
            data-testid="drawer-overlay"
            style={{ position: 'fixed', inset: 0, background: 'rgba(8,9,13,.6)', zIndex: 90 }}
          />
        )}

        {/* Sidebar */}
        <aside id="riq-sidebar-nav" style={{
          ...styles.sidebar,
          ...SIDEBAR_DARK_VARS,
          width: isMobile ? styles.sidebar.width : (contraido ? '76px' : '256px'),
          transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
          transition: isMobile ? 'transform 220ms ease' : 'width 200ms ease',
          boxShadow: isMobile && sidebarOpen ? '0 0 40px rgba(0,0,0,.5)' : 'none',
        }}>
          <div style={{ ...styles.sidebarLogo, alignItems: contraido ? 'center' : 'flex-start' }}>
            {isMobile && (
              <button onClick={() => setSidebarOpen(false)} className="kiq-focusable" aria-label="Cerrar menú de navegación" style={{
                position: 'absolute', top: '2px', right: '2px', border: 'none', background: 'none', color: TEXT_DIM, cursor: 'pointer', padding: '4px', flexShrink: 0,
              }}>
                <X size={18} />
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              {/* Marca real de KIQ (la misma imagen que ya usan los banners de
                  Finanzas/Ventas/Objetivos/Equipo) — nunca el placeholder SVG
                  de design-system/KIQMark.js, que el propio archivo documenta
                  como "marcador temporal, no es el logo final". Los mockups
                  de referencia de este rediseño (Shell App.dc.html, KIQ
                  Panel.dc.html) también usan esta imagen, no un placeholder.
                  Más grande cuando el sidebar está contraído (pedido explícito
                  tras ver el rediseño en vivo) — hay más espacio disponible
                  sin el wordmark al lado. */}
              <img src="/kiq-mark.png" alt="KIQ" style={{ width: contraido ? '40px' : '28px', height: contraido ? '40px' : '28px', borderRadius: contraido ? '12px' : '9px', objectFit: 'cover', flexShrink: 0, transition: 'width 200ms ease, height 200ms ease' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              {/* "KIQ" en gradiente de marca (mismo brand.gradient que el
                  resto del sistema) — nunca un color plano ahí, tal como
                  pide el README ("Gastro" + "KIQ" con gradiente de texto
                  90deg #4C86FF→#FF2D8A). El texto sigue concatenando a
                  "GastroKIQ" en el DOM (Layout.test.js lo busca así). */}
              {!contraido && (
                <span style={styles.logoTexto}>
                  Gastro<span style={styles.logoTextoKiq}>KIQ</span>
                </span>
              )}
            </div>
            {restaurante && !contraido && <span style={styles.logoSub}>{restaurante.nombre}</span>}
            {/* Contraer/Expandir — expandido: flota en la esquina del logo
                (position:absolute, no le importa su orden en el DOM). Contraído:
                `position:static` lo vuelve a meter en el flujo normal, y como
                ahora este bloque va DESPUÉS del logo (antes iba antes, por
                error — pedido explícito del usuario: "la flecha salga por
                debajo del logo"), en static queda debajo, nunca arriba. */}
            {!isMobile && (
              <button
                onClick={alternarColapso}
                className="kiq-focusable"
                aria-label={contraido ? 'Expandir menú' : 'Contraer menú'}
                title={contraido ? 'Expandir menú' : 'Contraer menú'}
                aria-expanded={!contraido}
                aria-controls="riq-sidebar-nav"
                style={{
                  position: contraido ? 'static' : 'absolute', top: contraido ? undefined : '-2px', right: contraido ? undefined : '-6px',
                  marginTop: contraido ? '8px' : undefined,
                  width: '24px', height: '24px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: radius.sm, border: `1px solid ${LINE2}`, background: 'var(--card-alt)', color: TEXT_DIM, cursor: 'pointer',
                }}
              >
                {contraido ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              </button>
            )}
          </div>

          <nav style={styles.nav}>
            {navGroups.map(group => (
              <div key={group.label} style={{ marginBottom: '14px' }}>
                {contraido ? <div style={styles.sectionDivider} /> : <div style={styles.sectionLabel}>{group.label}</div>}
                {group.items.filter(item => !item.mark || kiqPermitido).map(item => {
                  const activa = location.pathname === item.ruta;
                  const esKiq = !!item.mark;
                  return (
                    <Link key={item.ruta} to={item.ruta} onClick={cerrarDrawer} aria-current={activa ? 'page' : undefined}
                      aria-label={item.nombre} title={item.nombre}
                      className={activa ? 'kiq-focusable' : 'riq-nav-item kiq-focusable'}
                      style={{
                        ...styles.navItem,
                        justifyContent: contraido ? 'center' : 'flex-start',
                        background: activa ? ACTIVE_NAV_BG : (esKiq ? 'rgba(255,45,138,.07)' : undefined),
                        border: !activa && esKiq ? '1px solid rgba(255,45,138,.3)' : undefined,
                        borderLeft: activa ? `3px solid ${ACTIVE_NAV_BORDER}` : '3px solid transparent',
                        paddingLeft: '7px',
                        color: activa ? TEXT : undefined,
                        fontWeight: activa ? '800' : '600',
                      }}>
                      {esKiq
                        ? <img src="/kiq-mark.png" alt="" style={{ width: '20px', height: '20px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        : <item.icono size={16} style={{ flexShrink: 0 }} color={item.color} />}
                      <span style={contraido ? styles.visuallyHidden : { display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}>
                        <span style={contraido ? undefined : { overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.nombre}</span>
                        {item.sub && !contraido && <span style={{ fontSize: '10.5px', fontWeight: 500, color: TEXT_FAINT }}>{item.sub}</span>}
                      </span>
                    </Link>
                  );
                })}
              </div>
            ))}
            {usuario?.rol === 'admin' && (
              <div style={{ marginBottom: '14px' }}>
                {contraido ? <div style={styles.sectionDivider} /> : <div style={styles.sectionLabel}>CONFIGURACIÓN</div>}
                <Link to="/academia/admin" onClick={cerrarDrawer} aria-current={location.pathname === '/academia/admin' ? 'page' : undefined}
                  aria-label="Admin Academia" title="Admin Academia"
                  className={location.pathname === '/academia/admin' ? 'kiq-focusable' : 'riq-nav-item kiq-focusable'}
                  style={{
                    ...styles.navItem, justifyContent: contraido ? 'center' : 'flex-start',
                    background: location.pathname === '/academia/admin' ? ACTIVE_NAV_BG : undefined,
                    borderLeft: location.pathname === '/academia/admin' ? `3px solid ${ACTIVE_NAV_BORDER}` : '3px solid transparent',
                    paddingLeft: '7px',
                    color: location.pathname === '/academia/admin' ? TEXT : undefined,
                    fontWeight: location.pathname === '/academia/admin' ? '800' : '600',
                  }}>
                  <Settings size={16} style={{ flexShrink: 0 }} />
                  <span style={contraido ? styles.visuallyHidden : undefined}>Admin Academia</span>
                </Link>
              </div>
            )}
            {esSuperadmin && (
              <div style={{ marginBottom: '14px' }}>
                {contraido ? <div style={styles.sectionDivider} /> : <div style={styles.sectionLabel}>PLATAFORMA</div>}
                <Link to="/superadmin" onClick={cerrarDrawer} aria-current={location.pathname === '/superadmin' ? 'page' : undefined}
                  aria-label="Superadmin" title="Superadmin"
                  className={location.pathname === '/superadmin' ? 'kiq-focusable' : 'riq-nav-item kiq-focusable'}
                  style={{
                    ...styles.navItem, justifyContent: contraido ? 'center' : 'flex-start',
                    background: location.pathname === '/superadmin' ? ACTIVE_NAV_BG : undefined,
                    borderLeft: location.pathname === '/superadmin' ? `3px solid ${ACTIVE_NAV_BORDER}` : '3px solid transparent',
                    paddingLeft: '7px',
                    color: location.pathname === '/superadmin' ? TEXT : undefined,
                    fontWeight: location.pathname === '/superadmin' ? '800' : '600',
                  }}>
                  <ShieldCheck size={16} style={{ flexShrink: 0 }} />
                  <span style={contraido ? styles.visuallyHidden : undefined}>Superadmin</span>
                </Link>
              </div>
            )}
          </nav>

          <div style={styles.sidebarFooter}>
            <Link to="/perfil" onClick={cerrarDrawer} aria-current={location.pathname === '/perfil' ? 'page' : undefined}
              aria-label="Configuración" title="Configuración"
              className={location.pathname === '/perfil' ? 'kiq-focusable' : 'riq-nav-item kiq-focusable'}
              style={{
                ...styles.navItem, marginBottom: '10px', justifyContent: contraido ? 'center' : 'flex-start',
                background: location.pathname === '/perfil' ? ACTIVE_NAV_BG : undefined,
                borderLeft: location.pathname === '/perfil' ? `3px solid ${ACTIVE_NAV_BORDER}` : '3px solid transparent',
                paddingLeft: '7px',
                color: location.pathname === '/perfil' ? TEXT : undefined,
                fontWeight: location.pathname === '/perfil' ? '800' : '600',
              }}>
              <Settings size={16} style={{ flexShrink: 0 }} /><span style={contraido ? styles.visuallyHidden : undefined}>Configuración</span>
            </Link>
            <div style={{ ...styles.usuarioInfo, justifyContent: contraido ? 'center' : 'flex-start' }} title={contraido ? usuario?.nombre : undefined}>
              <div style={styles.avatar}>{usuario?.nombre?.charAt(0).toUpperCase()}</div>
              {!contraido && (
                <div style={{ minWidth: 0 }}>
                  <p style={styles.usuarioNombre}>{usuario?.nombre}</p>
                  <p style={styles.usuarioRol}>{usuario?.rol}</p>
                  {/* Email real solamente — nunca inventado; algunos usuarios
                      (ej. autenticados vía Google en el pasado) sí lo traen. */}
                  {usuario?.email && <p style={styles.usuarioEmail}>{usuario.email}</p>}
                </div>
              )}
            </div>
            <button onClick={logout} className="kiq-focusable" aria-label="Cerrar sesión" title="Cerrar sesión"
              style={{ ...styles.botonLogout, justifyContent: contraido ? 'center' : 'flex-start' }}>
              <LogOut size={15} /><span style={contraido ? styles.visuallyHidden : undefined}>Cerrar sesión</span>
            </button>
            {/* Sin link "Support": no existe una pantalla/flujo real de soporte
                hoy en la app — se omite en vez de agregar un CTA muerto.
                Shell (design_handoff_shell) — la tarjeta de plan (proCard,
                "Plan Gratis/Pro/Business") se ELIMINA por completo, junto con
                sus estilos y el import de Crown (ver README, sección "Footer"). */}
          </div>
        </aside>

        {/* Main — ya NO usa marginLeft fijo de 256px: sigue el ancho real del
            sidebar (76px contraído, 256px expandido, 0 en mobile).
            Fase 3.4.16 — `minHeight:0` agregado: sin esto, un flex item
            conserva su `min-height:auto` por defecto (se niega a encoger
            por debajo de la altura de SU PROPIO contenido), que es
            exactamente lo que impedía que el <div> de abajo
            (`flex:1, overflow:'auto'`) llegara a necesitar scroll propio —
            ver la corrección completa (y la causa raíz) en el comentario
            junto a .kiq-app-shell, arriba. */}
        <main style={{ marginLeft: isMobile ? 0 : (contraido ? '76px' : '256px'), transition: isMobile ? 'none' : 'margin-left 200ms ease', flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, fontFamily: "Inter, system-ui, sans-serif" }}>
            <ShellHeader location={location} isMobile={isMobile} onToggleSidebar={() => setSidebarOpen(open => !open)} usuario={usuario} restaurante={restaurante} kiqAbierto={kiqAbierto} onToggleKiq={onToggleKiq} kiqPermitido={kiqPermitido} />
            <div style={{ flex: 1, overflow: 'auto', padding: '22px 24px 32px' }}>
              <ErrorBoundary>{children}</ErrorBoundary>
            </div>
          </div>
        </main>

        {/* KIQ de escritorio — punto de entrada global sin cambios desde
            Fase 2 (sección 18 del pedido original): vive fuera de <main>
            para no scrollear con el contenido de la página, z-index 80
            (por debajo del overlay del drawer mobile [90], del sidebar
            [100] y de cualquier modal/overlay existente [≥900]).
            Posición/tamaño/comportamiento SIN NINGÚN CAMBIO respecto a
            antes de Fase 3.4.16 (sección 7 del pedido: "Desktop must
            retain existing floating KIQ trigger... positioning... sizing").

            Fase 3.4.16 — ya NUNCA se renderiza en mobile (`!isMobile`
            puro, sin ninguna lógica condicional de colisión): el punto de
            entrada móvil es ahora el botón dentro de ShellHeader (ver
            arriba) — exactamente un trigger primario visible en cada
            viewport, nunca los dos a la vez, verificado también por test
            (Layout.test.js, "Fase 3.4.16"). Todo el aparato de 6
            candidatos / MutationObserver / ResizeObserver / traspaso de
            foco de Fases 3.4.10-3.4.14-R queda eliminado — ver el reporte
            de esta fase para el detalle completo de causa raíz y
            remoción. */}
        <style>{`
          @media (prefers-reduced-motion: reduce) {
            .kiq-float-btn { transition: none !important; }
          }
        `}</style>
        {!isMobile && kiqPermitido && (
          <div style={{ position: 'fixed', zIndex: 80, bottom: '24px', right: '24px' }}>
            <KIQButton
              state="idle"
              notificationCount={0}
              label="KIQ"
              size={KIQ_SIZE_DESKTOP}
              ariaExpanded={kiqAbierto}
              onClick={onToggleKiq}
              className="kiq-float-btn"
            />
          </div>
        )}

        {kiqPermitido && <KIQPanel abierto={kiqAbierto} onCerrar={() => setKiqAbierto(false)} isMobile={isMobile} usuario={usuario} restaurante={restaurante} />}
      </div>
    </HeaderProvider>
  );
}

const styles = {
  // Fase 2.2 — overflowY:'auto' en el <aside> completo (nuevo). Antes solo
  // <nav> tenía su propio scroll interno (flex:1/overflowY:auto para listas
  // largas de navegación); el footer (Configuración/usuario/Cerrar sesión/
  // Plan) vivía FUERA de ese scroll y el <aside> mismo no tenía overflow
  // definido (overflow:visible por defecto) — en un viewport bajo (iPhone
  // SE, o con teclado/barra de Safari reduciendo el alto disponible), si
  // logo+nav+footer superaban el alto del drawer, el footer simplemente se
  // recortaba SIN scroll posible para alcanzarlo (bug real, confirmado por
  // lectura de código, no solo por la captura). Con overflowY:'auto' aquí,
  // el <aside> completo se vuelve scrolleable cuando su contenido excede el
  // alto disponible — <nav> conserva su propio scroll interno para listas
  // largas (patrón estándar de scroll anidado, correcto cuando el
  // contenedor exterior también puede necesitar scroll).
  sidebar: {
    width: 'min(256px, 82vw)', background: SIDEBAR_BG, borderRight: `1px solid ${SIDEBAR_BORDER}`,
    display: 'flex', flexDirection: 'column', overflowY: 'auto',
    position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 100,
    padding: '20px 14px 14px', gap: '4px',
    fontFamily: "'Hanken Grotesk', Inter, system-ui, sans-serif",
  },
  // Wordmark + KIQMark: centrado respecto al ancho completo del sidebar
  // (position:relative para que el botón de cierre en mobile se posicione
  // absoluto encima, sin desplazar el centrado del texto).
  sidebarLogo: {
    position: 'relative', width: '100%', display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center', padding: '10px 6px 24px',
  },
  logoTexto: {
    fontFamily: "'Hanken Grotesk', Inter, system-ui, sans-serif",
    fontSize: '22px', fontWeight: '800', letterSpacing: '-0.01em', color: text.primary,
    lineHeight: 1.15, whiteSpace: 'nowrap',
  },
  // "KIQ" del wordmark — gradiente de texto de marca (mismos colores que
  // brand.gradient), nunca un color plano: es la identidad visual de KIQ,
  // igual que el propio KIQMark/KIQButton/mark real.
  logoTextoKiq: {
    background: brand.gradient, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
  },
  logoSub: {
    marginTop: '5px', fontSize: '14px', fontWeight: '500', color: TEXT_FAINT,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
  },
  sectionLabel: { ...typography.label, color: 'var(--gray-faint)', padding: '0 10px 6px' },
  nav: { flex: 1, overflowY: 'auto', minHeight: 0 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: '11px',
    padding: '9px 10px', borderRadius: radius.md, cursor: 'pointer',
    fontSize: '13.5px', textDecoration: 'none',
    position: 'relative', transition: 'background 180ms ease, color 180ms ease, border-color 180ms ease',
  },
  // Shell (design_handoff_shell) — línea de 1px que reemplaza la etiqueta de
  // grupo cuando el sidebar está contraído (solo iconos).
  sectionDivider: { height: '1px', margin: '2px 14px 8px', background: LINE2 },
  // sr-only estándar — nunca display:none: el link conserva su nombre
  // accesible cuando el sidebar está contraído (aria-label/title en el
  // <Link> ya cubren lector de pantalla/tooltip; este span solo oculta el
  // texto VISUALMENTE, por si algún consumidor futuro lee el texto interno
  // en vez del aria-label).
  visuallyHidden: { position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 },
  sidebarFooter: {
    paddingTop: '12px', borderTop: `1px solid ${SIDEBAR_BORDER}`,
  },
  usuarioInfo: { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px', marginBottom: '4px' },
  avatar: {
    width: '26px', height: '26px', background: 'var(--border)', borderRadius: radius.sm,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: TEXT_DIM, fontWeight: '700', fontSize: '11px', flexShrink: 0,
  },
  usuarioNombre: { color: 'var(--text)', fontSize: '12px', fontWeight: '600', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  usuarioRol: { color: TEXT_FAINT, fontSize: '10.5px', margin: 0, textTransform: 'capitalize' },
  usuarioEmail: { color: 'var(--gray-faint)', fontSize: '10px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  botonLogout: {
    display: 'flex', alignItems: 'center', gap: '8px',
    color: TEXT_FAINT, background: 'none',
    border: 'none', cursor: 'pointer', fontSize: '12.5px', padding: '6px', width: '100%', borderRadius: radius.sm,
  },
};
